import { Button, ChevronRightIcon, Typography, UAEPassIcon } from '@/components/atoms';
import { HandHeartIcon } from '@/components/atoms/HandHeartIcon';
import { ConfirmationDialog, NumberPad, OTPInput } from '@/components/molecules';
import { OkDialog } from '@/components/molecules/dialogs/OkDialog';
import { LogoHeader } from '@/components/molecules/header/LogoHeader';
import { ENABLE_UAEPASS } from '@/core/constants/appConfig';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { useTranslation, useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { logger } from '@/core/logger';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { passcodeStorage } from '@/core/storage/passcodeStorage';
import { biometricAuth } from '@/core/wrappers/biometricAuth';
import { useFocusEffect } from '@react-navigation/native';
import { router, useNavigation } from 'expo-router';
import React, { useCallback, useEffect, useState } from 'react';
import { BackHandler, Platform, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { usePasscode } from '../hooks/usePasscode';

export function PasscodeLoginScreen() {
    useCatalogDynatraceView(DT_SCREEN.PASSCODE_LOGIN, 'passcode_login_viewed', true);
    const { t } = useTranslation();
    const colors = useUIColors();
    const insets = useSafeAreaInsets();
    const { isRTL } = useI18nContext();
    const navigation = useNavigation();
    const { verifyPasscode, resetPasscode, isLoading } = usePasscode();
    const [passcode, setPasscode] = useState('');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showNumberPad, setShowNumberPad] = useState(false);
    const [resetConfirmationVisible, setResetConfirmationVisible] = useState(false);
    const [resetDialogVisible, setResetDialogVisible] = useState(false);
    const [resetDialogMessage, setResetDialogMessage] = useState('');

    const maxLength = 4;

    // Check if biometric is enabled and attempt authentication on mount
    // Don't show loading screen - show UI behind biometric dialog
    useEffect(() => {
        const checkAndAuthenticate = async () => {
            try {
                const isEnabled = await passcodeStorage.getBiometricEnabled();

                if (isEnabled) {
                    // Small delay to ensure UI is rendered before showing biometric dialog
                    setTimeout(async () => {
                        const result = await biometricAuth.authenticateWithBiometric();
                        if (result.success) {
                            // Biometric success - get stored passcode and verify
                            try {
                                const storedPasscode = await passcodeStorage.getPasscode();
                                if (storedPasscode) {
                                    // Verify passcode with stored value
                                    const verifyResult = await verifyPasscode(storedPasscode);
                                    if (verifyResult.success) {
                                        // Navigate to home after successful verification
                                        router.replace('/(auth)/(home)');
                                    } else {
                                        // Verification failed - user can continue with manual entry
                                        logger.warn('PasscodeLoginScreen:Biometric passcode verification failed');
                                    }
                                } else {
                                    // No stored passcode - user can continue with manual entry
                                    logger.warn('PasscodeLoginScreen:No stored passcode for biometric');
                                }
                            } catch (error) {
                                logger.error('PasscodeLoginScreen:Failed to verify stored passcode', error);
                                // Continue - user can enter passcode manually
                            }
                        }
                        // If failed or cancelled, user can continue with passcode entry
                    }, 100);
                }
            } catch {
                // Silently fail - user can continue with passcode entry
            }
        };

        void checkAndAuthenticate();
    }, [verifyPasscode]);

    const handlePasscodeChange = (value: string) => {
        setPasscode(value);
        setErrorMessage(null);
        setSuccessMessage(null);
        // Hide number pad when passcode is cleared
        if (value.length === 0) {
            setShowNumberPad(false);
        }
    };

    const handleOTPFocus = () => {
        setShowNumberPad(true);
    };

    const handleNumberPress = (number: string | number) => {
        if (passcode.length < maxLength) {
            const newPasscode = passcode + String(number);
            setPasscode(newPasscode);
            setErrorMessage(null);
            setSuccessMessage(null);

            // Auto-submit when max length reached
            if (newPasscode.length === maxLength) {
                void handleSubmit(newPasscode);
            }
        }
    };

    const handleDeletePress = () => {
        if (passcode.length > 0) {
            setPasscode(passcode.slice(0, -1));
            setErrorMessage(null);
            setSuccessMessage(null);
        }
    };

    const handleSubmit = async (passcodeToVerify?: string) => {
        const codeToVerify = passcodeToVerify || passcode;
        if (codeToVerify.length !== maxLength) return;

        trackCatalogEvent({
            screen: DT_SCREEN.PASSCODE_LOGIN,
            actionName: 'passcode_login_submit',
            eventType: 'action',
        });
        try {
            const result = await verifyPasscode(codeToVerify);
            if (result.success) {
                // Show success state with green border
                setSuccessMessage(t('profile.passcodeSetSuccess'));
                setErrorMessage(null);
                // Navigate to home after a brief delay to show success state
                setTimeout(() => {
                    router.replace('/(auth)/(home)');
                }, 500);
            } else {
                trackCatalogEvent({
                    screen: DT_SCREEN.PASSCODE_LOGIN,
                    actionName: 'passcode_login_error',
                    eventType: 'action',
                });
                // Show error with red border - don't redirect to login
                setErrorMessage(t('profile.passcodeVerifyError'));
                setSuccessMessage(null);
                setPasscode('');
                setShowNumberPad(false);
            }
        } catch (err) {
            trackCatalogEvent({
                screen: DT_SCREEN.PASSCODE_LOGIN,
                actionName: 'passcode_login_error',
                eventType: 'action',
            });
            const errorMsg = err instanceof Error ? err.message : t('profile.passcodeVerifyError');
            setErrorMessage(errorMsg);
            setSuccessMessage(null);
            setPasscode('');
            setShowNumberPad(false);
        }
    };

    const handleContinueWithEmail = () => {
        // Navigate to email/password login screen with force flag to allow login even when authenticated
        router.push('/login?from=passcode-login');
    };

    const handleContinueWithUAEPass = () => {
        router.push('/uaepass');
    };

    const handleResetPasscode = () => {
        logger.info('PasscodeLoginScreen', 'Reset passcode confirmation requested');
        // Show confirmation dialog first
        setResetConfirmationVisible(true);
    };

    const handleResetConfirmation = async () => {
        setResetConfirmationVisible(false);
        logger.info('PasscodeLoginScreen', 'Reset passcode confirmed, calling API');
        try {
            // Call API to reset passcode flags on backend
            const result = await resetPasscode();
            
            if (result.success) {
                // Show success dialog with message from API
                setResetDialogMessage(result.message || t('profile.passcodeResetSuccess'));
                setResetDialogVisible(true);
            } else {
                // Show error dialog
                setResetDialogMessage(result.message || t('profile.passcodeResetError'));
                setResetDialogVisible(true);
            }
        } catch (error) {
            logger.error('PasscodeLoginScreen', 'Failed to reset passcode', error);
            // Show error dialog
            setResetDialogMessage(t('profile.passcodeResetError'));
            setResetDialogVisible(true);
        }
    };

    const handleResetCancel = () => {
        setResetConfirmationVisible(false);
        logger.info('PasscodeLoginScreen', 'Reset passcode cancelled');
    };

    const handleResetDialogOk = () => {
        setResetDialogVisible(false);
        // Navigate to login screen after dialog is closed
        router.replace('/login?from=passcode-login');
    };

    // Check if navigation can go back
    const navCanGoBack = typeof (navigation as any)?.canGoBack === 'function' ? (navigation as any).canGoBack() : false;

    // Handle back button press - close keypad if visible, otherwise use default navigation
    const handleBackPress = () => {
        if (showNumberPad) {
            // Close keypad if it's visible
            setShowNumberPad(false);
        } else if (navCanGoBack) {
            // Use default navigation behavior only if can go back
            if ((navigation as any)?.goBack) {
                (navigation as any).goBack();
            }
        }
    };

    // Show back button only when: NumberPad is visible OR can go back in navigation
    const shouldShowBackButton = showNumberPad || navCanGoBack;

    // Handle hardware back button (Android)
    useFocusEffect(
        useCallback(() => {
            const onBackPress = () => {
                if (showNumberPad) {
                    // Close keypad if it's visible
                    setShowNumberPad(false);
                    return true; // Prevent default behavior
                } else if (navCanGoBack) {
                    // Navigate back if possible
                    if ((navigation as any)?.goBack) {
                        (navigation as any).goBack();
                    }
                    return true; // Prevent default behavior
                }
                // Allow default behavior (might exit app or do nothing)
                return false;
            };

            if (Platform.OS === 'android') {
                const backHandler = BackHandler.addEventListener('hardwareBackPress', onBackPress);
                return () => backHandler.remove();
            }

            // Return empty cleanup function for non-Android platforms
            return () => { };
        }, [showNumberPad, navCanGoBack, navigation])
    );

    return (
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel1, paddingBottom: insets.bottom }]}>
            <LogoHeader
                showLanguageSwitch={true}
                showBackButton={shouldShowBackButton}
                onBackPress={shouldShowBackButton ? handleBackPress : undefined}
            />

            <View style={styles.content}>
                {/* Welcome Section */}
                <View style={styles.welcomeSection}>
                    <View style={styles.iconContainer}>
                        <HandHeartIcon size={40} color={colors.fontPrimary} />
                    </View>
                    <Typography variant="title1" color="primary" style={styles.welcomeTitle}>
                        {t('auth.welcomeBack')}
                    </Typography>
                    <Typography variant="body" color="primary" style={styles.welcomeSubtitle}>
                        {t('auth.signInToAccessProperties')}
                    </Typography>
                </View>

                {/* Passcode Input Section with Reset Button */}
                <View style={styles.passcodeContainer}>
                    <View style={styles.passcodeSection}>
                        <Typography variant="subhead" color="primary" style={styles.passcodeLabel}>
                            {t('profile.passcodeEnterPasscode')}
                        </Typography>
                        <OTPInput
                            value={passcode}
                            onChangeText={handlePasscodeChange}
                            onFocus={handleOTPFocus}
                            length={maxLength}
                            secure={true}
                            {...(errorMessage ? { errorMessage } : {})}
                            {...(successMessage ? { successMessage } : {})}
                            focusable={false}
                            autoFocus={false}
                            loading={isLoading}
                            disabled={isLoading}
                        />
                    </View>

                    {/* Reset passcode button - only show when NumberPad is hidden */}
                    {!showNumberPad && (
                        <View style={styles.resetRow}>
                            <Button
                                title={t('profile.passcodeResetPasscode')}
                                variant="text"
                                trailing={
                                    <View style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}>
                                        <ChevronRightIcon size={18} />
                                    </View>
                                }
                                onPress={handleResetPasscode}
                                disabled={isLoading}
                            />
                        </View>
                    )}
                </View>

                {/* Spacer */}
                {!showNumberPad && <View style={styles.spacer} />}

                {/* Action Buttons - Only show when NumberPad is hidden */}
                {!showNumberPad && (
                    <View style={styles.actionsSection}>
                        <Button
                            title={t('auth.continueWithEmail')}
                            variant="outline"
                            fullWidth
                            onPress={handleContinueWithEmail}
                            disabled={isLoading}
                        />

                        {/* OR Divider and UAE Pass Button - Only show if UAE Pass is enabled */}
                        {ENABLE_UAEPASS && (
                            <>
                                <View style={styles.orDivider}>
                                    <View style={[styles.dividerLine, { backgroundColor: colors.backgroundLevel4 }]} />
                                    <Typography variant="caption" color="tertiary" style={styles.orText}>
                                        {t('welcome.or')}
                                    </Typography>
                                    <View style={[styles.dividerLine, { backgroundColor: colors.backgroundLevel4 }]} />
                                </View>

                                <Button
                                    title={t('auth.continueWithUAEPass')}
                                    variant="primary"
                                    fullWidth
                                    leading={<UAEPassIcon size={18} variant="white" />}
                                    onPress={handleContinueWithUAEPass}
                                    disabled={isLoading}
                                />
                            </>
                        )}
                    </View>
                )}
            </View>

            {/* Number Pad - Only show when OTP input is focused */}
            {showNumberPad && (
                <View style={styles.numberPadContainer}>
                    <NumberPad
                        onNumberPress={handleNumberPress}
                        onDeletePress={handleDeletePress}
                        onSubmitPress={() => handleSubmit()}
                        disabled={isLoading}
                        loading={isLoading}
                    />
                </View>
            )}

            {/* Reset Passcode Confirmation Dialog */}
            <ConfirmationDialog
                visible={resetConfirmationVisible}
                message={t('profile.passcodeResetConfirmation')}
                onConfirm={handleResetConfirmation}
                onCancel={handleResetCancel}
            />

            {/* Reset Passcode Success/Error Dialog */}
            <OkDialog
                visible={resetDialogVisible}
                message={resetDialogMessage}
                onOk={handleResetDialogOk}
            />
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    content: {
        flex: 1,
        paddingHorizontal: 24,
        paddingTop: 48,
        gap: 48,
    },
    welcomeSection: {
        alignItems: 'center',
        gap: 16,
    },
    iconContainer: {
        padding: 8,
    },
    welcomeTitle: {
        textAlign: 'center',
    },
    welcomeSubtitle: {
        textAlign: 'center',
    },
    passcodeSection: {
        gap: 8,
        alignItems: 'center',
    },
    passcodeLabel: {
        textAlign: 'center',
    },
    spacer: {
        flex: 1,
    },
    actionsSection: {
        gap: 8,
        paddingBottom: 24,
    },
    orDivider: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 7,
        paddingVertical: 8,
    },
    dividerLine: {
        flex: 1,
        height: 2,
        borderRadius: 1,
    },
    orText: {
        paddingHorizontal: 4,
    },
    numberPadContainer: {
        paddingHorizontal: 24,
        paddingBottom: 24,
    },
    passcodeContainer: {
        gap: 4, // Small gap between passcode input and reset button
    },
    resetRow: {
        flexDirection: 'row',
        justifyContent: 'center',
        minHeight: 48, // Ensure button has enough space
    },
});

